import {
  type ContadorBuzonDTO,
  type NotificacionBuzonDTO,
  TAMANO_MAXIMO_PAGINA_BUZON,
} from '../../services/buzonService';
// Solo el tipo: `inteligenciaBuzon` usa los textos de este archivo, así que no hay ciclo en ejecución.
import type { ConteoBuzon } from './inteligenciaBuzon';
import { TIPO_EVENTO_BUZON, normalizarClaveTipoEvento } from './categoriasBuzon';

/** Ruta de la pantalla del buzón. */
export const RUTA_BUZON = '/buzon';

/** Parámetro de la URL con el que `/buzon` abre la lectura de una notificación (`?notificacion=12`). */
export const PARAMETRO_NOTIFICACION_BUZON = 'notificacion';

/** Ruta de la pantalla del buzón con la lectura de esa notificación abierta. */
export const rutaNotificacionBuzon = (idNotificacion: number): string =>
  `${RUTA_BUZON}?${PARAMETRO_NOTIFICACION_BUZON}=${idNotificacion}`;

/**
 * Desplazamiento sin barra visible, como en `Tabs`: la zona se sigue moviendo con la rueda, el dedo o
 * el teclado; solo se oculta la barra.
 */
export const CLASES_SCROLL_OCULTO = '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/** Ruta de las preferencias de correo del buzón. */
export const RUTA_PREFERENCIAS_BUZON = '/buzon/preferencias';

/** A partir de este número la campanita muestra "99+" para no desbordar el badge. */
const TOPE_BADGE = 99;

/**
 * @function textoBadgeBuzon
 * @description Texto del badge de la campanita: vacío sin pendientes y `99+` arriba del tope.
 */
export const textoBadgeBuzon = (pendientes: number): string => {
  if (!Number.isFinite(pendientes) || pendientes <= 0) {
    return '';
  }
  return pendientes > TOPE_BADGE ? `${TOPE_BADGE}+` : String(Math.trunc(pendientes));
};

/**
 * @function textoResumenBuzon
 * @description Línea de resumen ("4 pendientes · 2 sin leer"), en singular o plural.
 */
export const textoResumenBuzon = (pendientes: number, noLeidas: number): string => {
  const p = `${pendientes} ${pendientes === 1 ? 'pendiente' : 'pendientes'}`;
  if (noLeidas <= 0) {
    return p;
  }
  return `${p} · ${noLeidas} sin leer`;
};

const FORMATO_FECHA_CORTA = new Intl.DateTimeFormat('es-MX', {
  day: 'numeric',
  month: 'short',
});

const FORMATO_HORA = new Intl.DateTimeFormat('es-MX', {
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * @function formatearFechaBuzon
 * @description Fecha de una notificación para el buzón, en el huso del navegador: "Hace un momento",
 * "Hace 12 min", "Hace 3 h", "Ayer, 05:40 p.m." o "12 oct., 10:15 a.m.". Las fechas del SSO llegan en
 * UTC con `Z`. Una fecha inválida regresa cadena vacía para que la tarjeta se siga mostrando.
 * @param {string} fechaIso - Fecha tal como la manda el SSO.
 * @param {Date} [ahora] - Momento de referencia (inyectable para las pruebas).
 */
export const formatearFechaBuzon = (fechaIso: string, ahora: Date = new Date()): string => {
  const fecha = new Date(fechaIso);
  if (Number.isNaN(fecha.getTime())) {
    return '';
  }

  const minutos = Math.floor((ahora.getTime() - fecha.getTime()) / 60_000);
  if (minutos < 1) {
    return 'Hace un momento';
  }
  if (minutos < 60) {
    return `Hace ${minutos} min`;
  }

  const inicioDeHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  if (fecha >= inicioDeHoy) {
    return `Hace ${Math.floor(minutos / 60)} h`;
  }

  const inicioDeAyer = new Date(inicioDeHoy);
  inicioDeAyer.setDate(inicioDeAyer.getDate() - 1);
  if (fecha >= inicioDeAyer) {
    return `Ayer, ${FORMATO_HORA.format(fecha)}`;
  }

  return `${FORMATO_FECHA_CORTA.format(fecha)}, ${FORMATO_HORA.format(fecha)}`;
};

const FORMATO_FECHA_COMPLETA = new Intl.DateTimeFormat('es-MX', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * @function formatearFechaCompletaBuzon
 * @description Fecha completa de una notificación para el detalle ("16 de octubre de 2026, 09:47 a.m."),
 * en el huso del navegador. Una fecha inválida regresa cadena vacía.
 */
export const formatearFechaCompletaBuzon = (fechaIso: string): string => {
  const fecha = new Date(fechaIso);
  return Number.isNaN(fecha.getTime()) ? '' : FORMATO_FECHA_COMPLETA.format(fecha);
};

/**
 * @function textoTareasPorHacer
 * @description Parte del resumen de `/buzon` con las tareas: "1 tarea espera tu acción".
 */
export const textoTareasPorHacer = (tareas: number): string =>
  tareas === 1 ? '1 tarea espera tu acción' : `${tareas} tareas esperan tu acción`;

/**
 * @function textoAvisosSinDescartar
 * @description Parte del resumen de `/buzon` con los avisos: "1 aviso", "3 avisos".
 */
export const textoAvisosSinDescartar = (avisos: number): string => (avisos === 1 ? '1 aviso' : `${avisos} avisos`);

/**
 * @function textoConteoPestanaBuzon
 * @description Contador de una pestaña de `/buzon`: el número si es exacto; si solo es lo que trae la
 * página, "3+" a la vista y "al menos 3" para el lector de pantalla.
 */
export const textoConteoPestanaBuzon = ({ valor, exacto }: ConteoBuzon): { texto: string; etiqueta?: string } =>
  exacto ? { texto: String(valor) } : { texto: `${valor}+`, etiqueta: `al menos ${valor}` };

/**
 * @function textoPaginacionPendientesBuzon
 * @description Pie de "Por hacer" y "Avisos". Las dos pestañas salen de la misma consulta de
 * pendientes, así que el total no es de la pestaña: se dice qué cuenta ("173 pendientes en total", o
 * "en esta categoría" con una elegida).
 */
export const textoPaginacionPendientesBuzon = ({
  pagina,
  totalPaginas,
  total,
  conCategoria,
}: {
  pagina: number;
  totalPaginas: number;
  total: number;
  conCategoria: boolean;
}): string =>
  `Página ${pagina} de ${totalPaginas} · ${total} ${total === 1 ? 'pendiente' : 'pendientes'} ${
    conCategoria ? 'en esta categoría' : 'en total'
  }`;

/** Vacío de una página de "Por hacer" o "Avisos" cuando no se puede decir que la pestaña está vacía. */
export interface VacioPaginaBuzon {
  titulo: string;
  descripcion: string;
  /** Ofrecer "Página siguiente". */
  siguiente: boolean;
}

/**
 * @function vacioPaginaPendientesBuzon
 * @description Qué decir cuando "Por hacer" o "Avisos" no tiene nada en esta página. Si el conteo es
 * exacto y es cero, o todo cabe en una página, regresa `null`: aplica el vacío de siempre ("No tienes
 * tareas por hacer"). Si no, habla de la página: manda a la siguiente si la hay y, en la última, a las
 * anteriores (ahí puede haber, así que tampoco se afirma que no hay).
 */
export const vacioPaginaPendientesBuzon = ({
  tipo,
  conteo,
  pagina,
  totalPaginas,
}: {
  tipo: 'tareas' | 'avisos';
  conteo: ConteoBuzon;
  pagina: number;
  totalPaginas: number;
}): VacioPaginaBuzon | null => {
  if ((conteo.exacto && conteo.valor === 0) || totalPaginas <= 1) {
    return null;
  }
  const siguiente = pagina < totalPaginas;
  return {
    titulo: `No hay ${tipo} en esta página`,
    descripcion: siguiente ? 'Revisa la página siguiente.' : 'Revisa las páginas anteriores.',
    siguiente,
  };
};

/** Minúsculas y sin acentos, para que "asignacion" encuentre "asignación". */
const normalizarBusqueda = (texto: string | null | undefined): string =>
  (texto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

/**
 * @function coincideBusquedaBuzon
 * @description Si una notificación coincide con lo que se escribió en el buscador de `/buzon`. El SSO
 * no tiene búsqueda: se filtra lo ya cargado, por `Titulo` y `Mensaje`, sin importar mayúsculas ni
 * acentos. Sin texto, todo coincide.
 */
export const coincideBusquedaBuzon = (
  notificacion: Pick<NotificacionBuzonDTO, 'Titulo' | 'Mensaje'>,
  busqueda: string
): boolean => {
  const buscado = normalizarBusqueda(busqueda);
  if (!buscado) {
    return true;
  }
  return normalizarBusqueda(`${notificacion.Titulo ?? ''} ${notificacion.Mensaje ?? ''}`).includes(buscado);
};

/**
 * @function textoOrigenNotificacion
 * @description Quién originó la notificación: el nombre de usuario, "Sistema" para los avisos
 * automáticos, o vacío si el SSO no mandó ninguno.
 */
export const textoOrigenNotificacion = (
  notificacion: Pick<NotificacionBuzonDTO, 'UsuarioOrigen' | 'SistemaOrigen'>
): string => {
  if (notificacion.UsuarioOrigen) {
    return notificacion.UsuarioOrigen;
  }
  return notificacion.SistemaOrigen ? 'Sistema' : '';
};

/**
 * @function inicialesAutor
 * @description Iniciales del autor para un avatar: la primera letra de las dos primeras palabras.
 * "marbe" → "M"; "Abelardo Noguez Nuñez" → "AN". La usan `LineaTiempoHistorial` y la conversación
 * de revisión de materialidad, para que los avatares se vean igual.
 * @param {string} nombre - Nombre de quien originó el evento o escribió el mensaje.
 * @returns {string} Iniciales en mayúsculas, o vacío si no hay nombre.
 */
export const inicialesAutor = (nombre: string): string =>
  nombre
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((palabra) => Array.from(palabra)[0] ?? '')
    .join('')
    .toLocaleUpperCase('es-MX');

/**
 * Notificaciones que pide el panel de la campanita: una sola página, la más grande que acepta el
 * servicio. Aun así puede haber más pendientes que los cargados; el panel lo dice en su texto.
 */
export const TAMANO_PANEL_BUZON = TAMANO_MAXIMO_PAGINA_BUZON;

/**
 * Cómo se distingue lo no leído en las listas del buzón (tarjetas de aviso y pasos del plan): borde
 * izquierdo azul, fondo host y título en negritas. La fila lleva además `border-l-4`. Clases
 * completas para que Tailwind las encuentre.
 */
export const CLASES_LECTURA_BUZON = {
  noLeida: { fila: 'border-l-materialidad-azul-300 bg-host-50/60', titulo: 'font-bold' },
  leida: { fila: 'border-l-transparent bg-background-primary', titulo: 'font-medium' },
} as const;

/** Una tarjeta del panel abre su lectura en `/buzon`: al pasar el mouse su fondo se oscurece un poco. */
export const CLASES_TARJETA_CLICKEABLE_BUZON = 'transition-colors cursor-pointer hover:bg-host-50';

/**
 * El título de una tarjeta del panel es el botón que abre su lectura en `/buzon`: se ve como el texto
 * de siempre (hereda letra y color) y solo cambia al pasar el mouse y con el foco del teclado.
 */
export const CLASES_TITULO_TARJETA_BUZON =
  'text-left underline-offset-2 hover:underline cursor-pointer rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-host-300';

/** Lo que el botón del título agrega a su nombre accesible: que lleva a la lectura en el buzón. */
export const TEXTO_VER_DETALLE_BUZON = '(ver el detalle en el buzón)';

/** Grupos de fecha de la lista del panel, en este orden. */
export type TituloGrupoBuzon = 'Hoy' | 'Ayer' | 'Anteriores';

/** Un grupo de la lista del panel con sus notificaciones en el orden del SSO. */
export interface GrupoFechaBuzon<T> {
  titulo: TituloGrupoBuzon;
  elementos: T[];
}

/**
 * @function agruparPorFecha
 * @description Agrupa notificaciones en Hoy / Ayer / Anteriores con la fecha **local** de
 * `FechaCreacion` (llega en UTC con `Z`), con el mismo corte que `formatearFechaBuzon`. Conserva el
 * orden del SSO dentro de cada grupo y omite los grupos vacíos. Una fecha un poco en el futuro (reloj
 * del equipo atrasado) cuenta como Hoy; una fecha inválida va a Anteriores para que la tarjeta se siga
 * mostrando.
 * @param elementos - Notificaciones en el orden del SSO (más recientes primero).
 * @param ahora - Momento de referencia (inyectable para las pruebas).
 */
export const agruparPorFecha = <T extends Pick<NotificacionBuzonDTO, 'FechaCreacion'>>(
  elementos: readonly T[],
  ahora: Date = new Date()
): GrupoFechaBuzon<T>[] => {
  const inicioDeHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  const inicioDeAyer = new Date(inicioDeHoy);
  inicioDeAyer.setDate(inicioDeAyer.getDate() - 1);

  const grupos: GrupoFechaBuzon<T>[] = [
    { titulo: 'Hoy', elementos: [] },
    { titulo: 'Ayer', elementos: [] },
    { titulo: 'Anteriores', elementos: [] },
  ];
  for (const elemento of elementos) {
    const fecha = new Date(elemento.FechaCreacion);
    const indice = Number.isNaN(fecha.getTime()) ? 2 : fecha >= inicioDeHoy ? 0 : fecha >= inicioDeAyer ? 1 : 2;
    grupos[indice].elementos.push(elemento);
  }
  return grupos.filter((grupo) => grupo.elementos.length > 0);
};

/** Cómo se ve el globo de la campanita y cómo lo anuncia un lector de pantalla. */
export type EstadoGloboBuzon =
  | { tipo: 'numero'; texto: string; etiqueta: string }
  | { tipo: 'punto'; etiqueta: string }
  | { tipo: 'nada'; etiqueta: string };

/**
 * @function normalizarAccionablesBuzon
 * @description `Accionables` es un campo nuevo del SSO: si no llega o no es un número finito se toma
 * como 0, así nadie afirma tareas que el servidor no mandó.
 */
export const normalizarAccionablesBuzon = (accionables: unknown): number =>
  typeof accionables === 'number' && Number.isFinite(accionables) ? Math.trunc(accionables) : 0;

/**
 * @function estadoGloboBuzon
 * @description Globo de la campanita: número rojo con las no leídas (`99+` arriba de 99); si todo
 * está leído y hay pendientes que requieren acción, un punto ámbar sin número; en otro caso nada.
 * `Accionables` es un campo nuevo del SSO: mientras no llegue, no hay punto ámbar.
 */
export const estadoGloboBuzon = ({
  NoLeidas,
  Accionables,
}: Pick<ContadorBuzonDTO, 'NoLeidas' | 'Accionables'>): EstadoGloboBuzon => {
  const texto = textoBadgeBuzon(NoLeidas);
  if (texto) {
    return { tipo: 'numero', texto, etiqueta: `Buzón: ${Math.trunc(NoLeidas)} sin leer` };
  }

  const accionables = normalizarAccionablesBuzon(Accionables);
  if (accionables > 0) {
    const verbo = accionables === 1 ? 'requiere' : 'requieren';
    return { tipo: 'punto', etiqueta: `Buzón: todo leído, ${accionables} ${verbo} acción` };
  }

  return { tipo: 'nada', etiqueta: 'Buzón: estás al día' };
};

/**
 * @function textoAvisoNuevasBuzon
 * @description Texto fijo del aviso de notificaciones nuevas. Nunca lleva el título ni el mensaje de
 * una notificación: `CustomSnackbar` pinta su mensaje como HTML.
 */
export const textoAvisoNuevasBuzon = (nuevas: number): string =>
  nuevas === 1 ? 'Tienes 1 notificación nueva' : `Tienes ${nuevas} notificaciones nuevas`;

/** Nombre corto del tipo de evento para los avisos de conciliación y reasignación; `null` si no es uno de ellos. */
const ETIQUETAS_TIPO_EVENTO: Partial<Record<string, string>> = {
  [TIPO_EVENTO_BUZON.FacturaAsignada]: 'Factura asignada',
  [TIPO_EVENTO_BUZON.FacturaReasignada]: 'Factura reasignada',
  [TIPO_EVENTO_BUZON.SolicitudReasignacion]: 'Solicitud de reasignación',
  [TIPO_EVENTO_BUZON.SolicitudReasignacionRechazada]: 'Reasignación rechazada',
  [TIPO_EVENTO_BUZON.MaterialidadRelacionada]: 'Materialidad agregada',
  [TIPO_EVENTO_BUZON.FacturaCanceladaSat]: 'Factura cancelada en el SAT',
  [TIPO_EVENTO_BUZON.FacturaNoReconocidaCancelada]: 'No reconocida cancelada',
  [TIPO_EVENTO_BUZON.FacturaNoReconocidaProveedorNotificado]: 'Cancelación solicitada al proveedor',
};

/**
 * @function etiquetaTipoEvento
 * @description Nombre corto del tipo de aviso ("Materialidad agregada") para mostrarlo en el detalle; `null`
 * si el front no lo conoce (se usa entonces `NombreTipoEvento` del SSO).
 */
export const etiquetaTipoEvento = (clave: string | null | undefined): string | null =>
  ETIQUETAS_TIPO_EVENTO[normalizarClaveTipoEvento(clave)] ?? null;
